import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import { getErrorMessage } from '../utils/errorHandler';
import { formatPKR } from '../utils/currencyFormatter';
import { API } from '../config';
import axios from 'axios';
import { HiArrowRight, HiChevronDown, HiChevronRight, HiPlus, HiTrash, HiDocumentText } from 'react-icons/hi';
import './StockTransfer.css';

const PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [50, 100, 200];

const getProductName = (row) => row.product?.name || row.product_name || '';
const getProductCode = (row) =>
  row.product?.product_code ?? row.product?.productCode ?? row.product_code ?? '';
const getVariantLabel = (row) => {
  const v = row.variant || row;
  const parts = [v.color, v.size].filter(Boolean);
  return parts.length ? parts.join(' / ') : 'Default';
};
const getAvailableQty = (row) => parseFloat(row.quantity ?? 0) || 0;
const getCostPrice = (row) => {
  const v = row.variant || row;
  const p = row.product || row;
  const vCost = v.cost_price ?? v.costPrice;
  const pCost = p?.cost_price ?? p?.costPrice;
  const num = parseFloat(vCost ?? pCost ?? 0);
  return Number.isFinite(num) ? num : 0;
};

const StockTransfer = () => {
  const { user } = useAuth();
  const { currentBranch } = useBranch();
  const { success, error } = useNotification();
  const isMainBranch = currentBranch && (currentBranch.is_main === true || currentBranch.isMain === true);

  const [branches, setBranches] = useState([]);
  const [fromBranchId, setFromBranchId] = useState('');
  const [toBranchId, setToBranchId] = useState('');
  const [inventory, setInventory] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [expandedProductId, setExpandedProductId] = useState(null);
  const [variantForm, setVariantForm] = useState({}); // variantId -> { qty: '', price: '' }
  const [cart, setCart] = useState([]); // { productId, productCode, productName, variantId, variantLabel, quantity, unitPrice, maxAvailable }
  const getTodayStr = () => new Date().toISOString().split('T')[0];
  const [transferDate, setTransferDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [showConfirmStep1, setShowConfirmStep1] = useState(false);
  const [showConfirmStep2, setShowConfirmStep2] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/branches`);
      let list = [];
      if (Array.isArray(res.data?.branches)) list = res.data.branches;
      else if (Array.isArray(res.data?.data?.branches)) list = res.data.data.branches;
      else if (Array.isArray(res.data)) list = res.data;
      setBranches(list);
      if (list.length > 0) {
        const main = list.find((b) => b.is_main || b.isMain);
        setFromBranchId(main ? String(main.id) : String(list[0].id));
      }
    } catch (err) {
      error(getErrorMessage(err));
      setBranches([]);
    }
  }, [error]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [fromBranchId, searchTerm]);

  useEffect(() => {
    if (!fromBranchId) {
      setInventory([]);
      setTotalCount(0);
      setCart([]);
      setExpandedProductId(null);
      setVariantForm({});
      return;
    }
    setInventoryLoading(true);
    const offset = (page - 1) * pageSize;
    const params = new URLSearchParams({
      limit: String(pageSize),
      offset: String(offset),
      only_with_stock: 'true'
    });
    if (searchTerm) params.set('search', searchTerm);
    axios
      .get(`${API}/inventory/branch/${fromBranchId}?${params.toString()}`)
      .then((res) => {
        const list = res.data?.inventory ?? [];
        const total = res.data?.total ?? (Array.isArray(list) ? list.length : 0);
        setInventory(Array.isArray(list) ? list : []);
        setTotalCount(total);
      })
      .catch((err) => {
        error(getErrorMessage(err));
        setInventory([]);
        setTotalCount(0);
      })
      .finally(() => setInventoryLoading(false));
  }, [fromBranchId, page, pageSize, searchTerm, error]);

  const productsGrouped = useMemo(() => {
    const map = {};
    inventory.forEach((row) => {
      const pid = row.product_id ?? row.product?.id;
      if (pid == null) return;
      const productType = row.product?.product_type ?? row.product?.productType;
      if (productType !== 'stitched') return; // Only stitched products can be transferred
      if (!map[pid]) {
        map[pid] = {
          productId: pid,
          productCode: getProductCode(row) || '—',
          productName: getProductName(row) || '—',
          productType,
          variants: []
        };
      }
      map[pid].variants.push({
        variantId: row.variant?.id ?? row.variant_id,
        variantLabel: getVariantLabel(row),
        available: getAvailableQty(row),
        costPrice: getCostPrice(row),
        row
      });
    });
    return Object.values(map);
  }, [inventory]);

  const availableBranchesTo = branches.filter((b) => String(b.id) !== String(fromBranchId));
  const fromBranch = branches.find((b) => String(b.id) === String(fromBranchId));
  const toBranch = branches.find((b) => String(b.id) === String(toBranchId));

  const setVariantFormField = (variantId, field, value) => {
    setVariantForm((prev) => ({
      ...prev,
      [variantId]: { ...(prev[variantId] || {}), [field]: value }
    }));
  };

  const addToCart = (productId, productCode, productName, variantId, variantLabel, available, costPrice) => {
    const form = variantForm[variantId] || {};
    const qty = parseFloat(form.qty) || 0;
    const price = form.price !== undefined && form.price !== '' ? parseFloat(form.price) : costPrice;
    if (qty <= 0) {
      error('Enter a quantity greater than 0.');
      return;
    }
    if (qty > available) {
      error(`Quantity cannot exceed available stock (${available}).`);
      return;
    }
    setCart((prev) => [
      ...prev,
      {
        productId,
        productCode,
        productName,
        variantId,
        variantLabel,
        quantity: qty,
        unitPrice: Number.isFinite(price) ? price : costPrice,
        maxAvailable: available
      }
    ]);
    setVariantForm((prev) => {
      const next = { ...prev };
      delete next[variantId];
      return next;
    });
  };

  const removeFromCart = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const clearCart = () => setCart([]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  const runTransfer = async () => {
    if (!fromBranchId || !toBranchId || cart.length === 0) return;
    setShowConfirmStep2(false);
    setSubmitting(true);
    let done = 0;
    let failed = false;
    for (const item of cart) {
      try {
        const dateToSend = (transferDate && String(transferDate).trim()) || getTodayStr();
        const payload = {
          fromBranchId: parseInt(fromBranchId, 10),
          toBranchId: parseInt(toBranchId, 10),
          variantId: item.variantId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          transferDate: dateToSend
        };
        await axios.post(`${API}/inventory/transfer`, payload);
        done++;
      } catch (err) {
        error(getErrorMessage(err));
        failed = true;
        break;
      }
    }
    setSubmitting(false);
    if (!failed) {
      success(`Transferred ${done} item(s). Branch ledgers updated.`);
      setCart([]);
      setPage(1);
      setInventoryLoading(true);
      const params = new URLSearchParams({ limit: String(pageSize), offset: '0', only_with_stock: 'true' });
      if (searchTerm) params.set('search', searchTerm);
      axios
        .get(`${API}/inventory/branch/${fromBranchId}?${params.toString()}`)
        .then((res) => {
          setInventory(res.data?.inventory ?? []);
          setTotalCount(res.data?.total ?? 0);
        })
        .catch(() => {})
        .finally(() => setInventoryLoading(false));
    }
  };

  const handleTransferClick = (e) => {
    e.preventDefault();
    if (!fromBranchId || !toBranchId) {
      error('Select both From and To branch.');
      return;
    }
    if (fromBranchId === toBranchId) {
      error('From and To branch must be different.');
      return;
    }
    if (cart.length === 0) {
      error('Add at least one item to the cart.');
      return;
    }
    setShowConfirmStep1(true);
  };

  const businessIdForHistory = user?.business_id ?? user?.businessId ?? fromBranch?.business_id ?? fromBranch?.businessId;

  const fetchTransferHistory = useCallback(async () => {
    const bid = businessIdForHistory || (branches[0]?.business_id ?? branches[0]?.businessId);
    if (!bid) return;
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/transfers/business/${bid}`);
      setTransferHistory(res.data?.transfers ?? res.data ?? []);
    } catch (err) {
      error(getErrorMessage(err));
      setTransferHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [businessIdForHistory, branches, error]);

  useEffect(() => {
    if (showHistoryModal && (businessIdForHistory || branches?.length)) fetchTransferHistory();
  }, [showHistoryModal, businessIdForHistory, branches?.length, fetchTransferHistory]);

  return (
    <Layout>
      <div className="stock-transfer-page">
        {!isMainBranch ? (
          <div className="stock-transfer-restricted">
            <h1>Stock Transfer</h1>
            <p className="stock-transfer-restricted-message">
              Only the main branch can transfer stock.
              {currentBranch ? (
                <> You are currently viewing <strong>{currentBranch.name}</strong>. Switch to the main branch in the header to transfer stock.</>
              ) : (
                <> Select the main branch in the header to transfer stock to other branches.</>
              )}
            </p>
          </div>
        ) : (
        <>
        <div className="stock-transfer-header">
          <div className="stock-transfer-header-row">
            <h1>Stock Transfer</h1>
            <button
              type="button"
              className="btn-secondary stock-transfer-history-btn"
              onClick={() => setShowHistoryModal(true)}
            >
              <HiDocumentText /> Transfer history
            </button>
          </div>
          <p className="stock-transfer-desc">
            Only <strong>stitched</strong> products can be transferred. Click a product to show variants, add items to the cart, then transfer. Branch ledgers (sending and receiving) are updated automatically.
          </p>
        </div>

        <div className="stock-transfer-controls">
          <div className="stock-transfer-branches">
            <div className="form-group">
              <label>From branch</label>
              <div className="stock-transfer-from-branch-readonly">
                {fromBranch ? `${fromBranch.name}${fromBranch.is_main || fromBranch.isMain ? ' (Main)' : ''}` : (fromBranchId ? 'Main branch' : '—')}
              </div>
            </div>
            <span className="stock-transfer-arrow">
              <HiArrowRight />
            </span>
            <div className="form-group">
              <label>To branch</label>
              <select
                value={toBranchId}
                onChange={(e) => setToBranchId(e.target.value)}
                className="stock-transfer-select"
              >
                <option value="">Select branch</option>
                {availableBranchesTo.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group stock-transfer-search">
            <label>Search products</label>
            <input
              type="text"
              placeholder="Product name, code, or variant"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="stock-transfer-search-input"
            />
          </div>
        </div>

        <div className="stock-transfer-layout">
          <div className="stock-transfer-product-list">
            {inventoryLoading ? (
              <Loader message="Loading inventory…" variant="block" />
            ) : !fromBranchId ? (
              <div className="stock-transfer-empty">Select a source branch to see products.</div>
            ) : productsGrouped.length === 0 ? (
              <div className="stock-transfer-no-rows">
                {totalCount === 0 ? 'No stock in this branch.' : 'No matching products with stock.'}
              </div>
            ) : (
              <div className="stock-transfer-products">
                {productsGrouped
                .filter((product) => product.variants.some((v) => (v.available ?? 0) > 0))
                .map((product) => {
                  const variantsInStock = product.variants.filter((v) => (v.available ?? 0) > 0);
                  const isExpanded = expandedProductId === product.productId;
                  return (
                    <div key={product.productId} className={`stock-transfer-product-block ${isExpanded ? 'expanded-block' : ''}`}>
                      <div
                        className={`stock-transfer-product-row ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => setExpandedProductId((p) => (p === product.productId ? null : product.productId))}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setExpandedProductId((p) => (p === product.productId ? null : product.productId));
                          }
                        }}
                      >
                        <span className="stock-transfer-product-chevron">
                          {isExpanded ? <HiChevronDown /> : <HiChevronRight />}
                        </span>
                        <span className="stock-transfer-product-code">{product.productCode}</span>
                        <span className="stock-transfer-product-name">{product.productName}</span>
                        <span className="stock-transfer-product-variant-count">
                          {variantsInStock.length} variant{variantsInStock.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {isExpanded && variantsInStock.length > 0 && (
                        <div className="stock-transfer-variants">
                          <div className="stock-transfer-variants-header">
                            <span>Variant</span>
                            <span className="col-num">Available</span>
                            <span className="col-num">Cost price</span>
                            <span className="col-num">Qty</span>
                            <span className="col-num">Transfer price</span>
                            <span className="col-action" />
                          </div>
                          {variantsInStock.map((v) => {
                            const form = variantForm[v.variantId] || {};
                            return (
                              <div key={v.variantId} className="stock-transfer-variant-row" onClick={(e) => e.stopPropagation()}>
                                <span>{v.variantLabel}</span>
                                <span className="col-num">{v.available}</span>
                                <span className="col-num cost-price-cell">{formatPKR(v.costPrice)}</span>
                                <span className="col-num">
                                  <input
                                    type="number"
                                    min={0}
                                    max={v.available}
                                    step={1}
                                    value={form.qty ?? ''}
                                    onChange={(e) => setVariantFormField(v.variantId, 'qty', e.target.value)}
                                    placeholder="0"
                                    className="stock-transfer-qty-input"
                                  />
                                </span>
                                <span className="col-num">
                                  <input
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    value={form.price ?? ''}
                                    onChange={(e) => setVariantFormField(v.variantId, 'price', e.target.value)}
                                    placeholder={v.costPrice ? formatPKR(v.costPrice) : '0'}
                                    className="stock-transfer-price-input"
                                  />
                                </span>
                                <span className="col-action">
                                  <button
                                    type="button"
                                    className="stock-transfer-add-cart-btn"
                                    onClick={() =>
                                      addToCart(
                                        product.productId,
                                        product.productCode,
                                        product.productName,
                                        v.variantId,
                                        v.variantLabel,
                                        v.available,
                                        v.costPrice
                                      )
                                    }
                                    title="Add to cart"
                                  >
                                    <HiPlus /> Add
                                  </button>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {totalCount > 0 && (
              <div className="stock-transfer-pagination">
                <div className="stock-transfer-pagination-info">
                  Showing {rangeStart}–{rangeEnd} of {totalCount} variants
                </div>
                <div className="stock-transfer-pagination-controls">
                  <label className="page-size-label">
                    Per page:
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="page-size-select"
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </label>
                  <div className="pagination-buttons">
                    <button
                      type="button"
                      className="btn-pagination"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1 || inventoryLoading}
                      aria-label="Previous page"
                    >
                      ←
                    </button>
                    <span className="pagination-page">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      className="btn-pagination"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages || inventoryLoading}
                      aria-label="Next page"
                    >
                      →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="stock-transfer-cart-panel">
            <h3 className="stock-transfer-cart-title">Transfer cart</h3>
            {toBranchId ? (
              <p className="stock-transfer-cart-to">To: <strong>{toBranch?.name}</strong></p>
            ) : (
              <p className="stock-transfer-cart-hint">Select a destination branch.</p>
            )}
            {cart.length === 0 ? (
              <div className="stock-transfer-cart-empty">No items. Click a product, then add variants to the cart.</div>
            ) : (
              <>
                <div className="stock-transfer-cart-transfer-date">
                  <label>Transfer date (optional)</label>
                  <input
                    type="date"
                    value={transferDate || getTodayStr()}
                    onChange={(e) => setTransferDate(e.target.value || getTodayStr())}
                    className="stock-transfer-date-input"
                  />
                  <span className="stock-transfer-date-hint">Used for branch/party ledger. Defaults to today.</span>
                </div>
                <ul className="stock-transfer-cart-list">
                  {cart.map((item, index) => (
                    <li key={`${item.variantId}-${index}`} className="stock-transfer-cart-item">
                      <div className="stock-transfer-cart-item-main">
                        <span className="stock-transfer-cart-item-name">{item.productName}</span>
                        <span className="stock-transfer-cart-item-variant">{item.variantLabel}</span>
                        <span className="stock-transfer-cart-item-qty">{item.quantity} × {formatPKR(item.unitPrice)}</span>
                      </div>
                      <button
                        type="button"
                        className="stock-transfer-cart-remove"
                        onClick={() => removeFromCart(index)}
                        aria-label="Remove"
                      >
                        <HiTrash />
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="stock-transfer-cart-actions">
                  <button
                    type="button"
                    className="btn-primary stock-transfer-submit"
                    onClick={handleTransferClick}
                    disabled={submitting || !toBranchId}
                  >
                    {submitting ? 'Transferring…' : 'Transfer stock'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={clearCart}
                    disabled={submitting}
                  >
                    Clear cart
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* First confirmation: show full list of products/variants */}
        {showConfirmStep1 && (
          <div className="modal-overlay" onClick={() => setShowConfirmStep1(false)}>
            <div className="modal-content stock-transfer-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Confirm transfer</h3>
              <p className="stock-transfer-confirm-dir">
                From <strong>{fromBranch?.name}</strong> → To <strong>{toBranch?.name}</strong>
              </p>
              <p className="stock-transfer-confirm-sub">Review the items below. You will be asked to confirm once more.</p>
              <div className="stock-transfer-confirm-list-wrap">
                <table className="stock-transfer-confirm-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Variant</th>
                      <th className="col-num">Qty</th>
                      <th className="col-num">Unit price</th>
                      <th className="col-num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.map((item, idx) => (
                      <tr key={`${item.variantId}-${idx}`}>
                        <td>{item.productName}</td>
                        <td>{item.variantLabel}</td>
                        <td className="col-num">{item.quantity}</td>
                        <td className="col-num">{formatPKR(item.unitPrice)}</td>
                        <td className="col-num">{formatPKR(item.quantity * (item.unitPrice || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="stock-transfer-confirm-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowConfirmStep1(false)}>Cancel</button>
                <button type="button" className="btn-primary" onClick={() => { setShowConfirmStep1(false); setShowConfirmStep2(true); }}>Confirm</button>
              </div>
            </div>
          </div>
        )}

        {/* Second confirmation: final confirmation */}
        {showConfirmStep2 && (
          <div className="modal-overlay" onClick={() => setShowConfirmStep2(false)}>
            <div className="modal-content stock-transfer-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Final confirmation</h3>
              <p className="stock-transfer-confirm-warn">Are you sure you want to transfer these items? This action will update stock and branch ledgers.</p>
              <div className="stock-transfer-confirm-list-wrap">
                <table className="stock-transfer-confirm-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Variant</th>
                      <th className="col-num">Qty</th>
                      <th className="col-num">Unit price</th>
                      <th className="col-num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.map((item, idx) => (
                      <tr key={`${item.variantId}-${idx}`}>
                        <td>{item.productName}</td>
                        <td>{item.variantLabel}</td>
                        <td className="col-num">{item.quantity}</td>
                        <td className="col-num">{formatPKR(item.unitPrice)}</td>
                        <td className="col-num">{formatPKR(item.quantity * (item.unitPrice || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="stock-transfer-confirm-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowConfirmStep2(false)} disabled={submitting}>Cancel</button>
                <button type="button" className="btn-primary" onClick={runTransfer} disabled={submitting}>
                  {submitting ? 'Transferring…' : 'Yes, transfer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Transfer history modal */}
        {showHistoryModal && (
          <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
            <div className="modal-content stock-transfer-history-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Transfer history</h3>
              {historyLoading ? (
                <Loader message="Loading transfer history…" variant="block" />
              ) : (
                <div className="stock-transfer-history-wrap">
                  {transferHistory.length === 0 ? (
                    <p className="stock-transfer-history-empty">No transfers yet.</p>
                  ) : (
                    <table className="stock-transfer-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Product</th>
                          <th>Variant</th>
                          <th className="col-num">Qty</th>
                          <th className="col-num">Price</th>
                          <th>Ref</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transferHistory.map((t) => {
                          const from = t.from_branch || t.fromBranch;
                          const to = t.to_branch || t.toBranch;
                          const prod = t.product;
                          const vari = t.variant;
                          const variantStr = vari ? [vari.color, vari.size].filter(Boolean).join(' / ') || 'Default' : '—';
                          const dateStr = t.transfer_date || t.transferDate ? new Date(t.transfer_date || t.transferDate).toLocaleDateString() : '—';
                          return (
                            <tr key={t.id}>
                              <td>{dateStr}</td>
                              <td>{from?.name ?? '—'}</td>
                              <td>{to?.name ?? '—'}</td>
                              <td>{prod?.name ?? prod?.productCode ?? '—'}</td>
                              <td>{variantStr}</td>
                              <td className="col-num">{t.quantity}</td>
                              <td className="col-num">{t.unit_price != null ? formatPKR(t.unit_price) : '—'}</td>
                              <td>{t.transfer_number || t.transferNumber || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowHistoryModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {fromBranch && toBranch && (
          <p className="stock-transfer-hint">
            Only the main branch can transfer. Branch ledger entries are created for both <strong>{fromBranch.name}</strong> and <strong>{toBranch.name}</strong>.
          </p>
        )}
        </>
        )}
      </div>
    </Layout>
  );
};

export default StockTransfer;
